import { styled } from "goober";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { isEditableTarget } from "../lib/isEditableTarget";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { Text } from "./Text";
import { isInAppNavigation } from "./Link";

export type SortDirection = "asc" | "desc";

// Stable empty default so the TableRow memo comparator's referential check on
// numericColumns holds for every caller that doesn't pass it — a fresh `[]`
// default would break row memoization across all existing ListTables.
const NO_NUMERIC_COLUMNS: readonly string[] = [];

type ColumnSize =
  | "flex"
  | "md"
  | "sm"
  | "content"
  | "fixed-activity"
  | "members";

const columnSizeValues: Record<ColumnSize, string> = {
  flex: "minmax(200px, 3fr)",
  md: "minmax(80px, 1fr)",
  sm: "minmax(60px, 0.75fr)",
  content: "max-content",
  "fixed-activity": "104px",
  members: "128px",
};

const Wrapper = styled("div")`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  flex: 1;
`;

const Table = styled("div")`
  display: grid;
`;

const Header = styled("div")`
  display: contents;
`;

const HeaderCell = styled("div")`
  position: sticky;
  top: var(--sticky-top, 0px);
  z-index: 1;
  background: var(--surface-primary);
  box-shadow: 0 0.5px 0 var(--border-alpha);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 48px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:first-child {
    padding-left: 24px;
  }
  &:last-child {
    padding-right: 24px;
  }

  @media (max-width: 768px) {
    &:first-child {
      padding-left: 12px;
    }
    &:last-child {
      padding-right: 12px;
    }
  }
`;

export const SortArrow = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  opacity: 0;
  transition: transform var(--motion-fast) var(--ease-out);
  transform: rotate(180deg);

  &[data-active="true"] {
    opacity: 1;
  }

  &[data-direction="desc"] {
    transform: rotate(0deg);
  }
`;

export const SortableHeaderContent = styled("span")`
  display: flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  user-select: none;
  padding: 4px 6px 4px 8px;
  margin: -4px -6px -4px -8px;
  border-radius: 9999px;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    color: var(--text-primary);

    ${SortArrow}:not([data-active="true"]) {
      opacity: 0.2;
    }
  }
`;

const Body = styled("div")`
  display: contents;
`;

const Footer = styled("div")`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  z-index: 1;
  background: var(--surface-primary);
  box-shadow: 0 -0.5px 0 var(--border-alpha);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  min-height: 36px;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const EmptyStateWrapper = styled("div")`
  flex: 1;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const rowStyles = `
  display: contents;
  --cell-bg: var(--surface-primary);
  &:hover,
  &:focus-within,
  &[data-selected="true"] {
    --cell-bg: var(--surface-hover);
    > * {
      background: var(--surface-hover);
      /* Paint the whole highlighted row to avoid seams at fractional zoom. */
      content-visibility: visible;
    }
  }
`;

const Row = styled(Link)`
  ${rowStyles}
  text-decoration: none;
  color: initial;
`;

const StaticRow = styled("div")`
  ${rowStyles}
`;

const ClickableRow = styled(StaticRow)`
  > * {
    cursor: pointer;
  }
`;

const Cell = styled("div")`
  /* Cells must set an explicit color; the inherited fallback is the CanvasText
     system color, which renders wrong in Firefox/Safari dark mode because
     color-scheme is applied at runtime. */
  color: var(--text-primary);
  min-width: 0; /* allow text truncation in flexible columns */
  /* Lets the browser skip layout and paint of offscreen cells; rows can't
     host this because display: contents generates no box. */
  content-visibility: auto;
  padding: 0 12px;
  height: 80px;
  border-bottom: 0.5px solid var(--border-alpha);
  align-items: center;
  display: flex;
  z-index: 0; /* ensure contents don't get in front of sticky headers */

  &:first-child {
    padding-left: 24px;
  }
  &:last-child {
    padding-right: 24px;
  }

  @media (max-width: 768px) {
    height: 64px;
    &:first-child {
      padding-left: 12px;
    }
    &:last-child {
      padding-right: 12px;
    }
  }

  &[data-collapsed] {
    overflow: hidden;
    transition:
      height var(--motion-base) var(--ease-out),
      opacity var(--motion-base) var(--ease-out),
      border-bottom-color var(--motion-base) var(--ease-out);
  }
  &[data-collapsed="true"] {
    height: 0;
    opacity: 0;
    border-bottom-color: transparent;
  }
`;

type CellValue =
  | React.ReactNode
  | ((hasMouse: boolean) => React.ReactNode)
  | (() => void);

type Item = {
  key: string;
  link?: string;
  onClick?: () => void;
  onNavigate?: () => void;
  collapsed?: boolean;
  revealIndex?: number;
  [column: string]: CellValue;
};

function TableRowBase<T extends Item>({
  item,
  columnKeys,
  selected,
  numericColumns,
}: {
  item: T;
  columnKeys: Array<
    Exclude<
      keyof T,
      "key" | "link" | "onClick" | "onNavigate" | "collapsed" | "revealIndex"
    >
  >;
  selected: boolean;
  numericColumns: readonly string[];
}) {
  const isMobile = useIsMobile();
  const [mouseHovered, setMouseHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const hasMouse = isMobile || mouseHovered || focusWithin || selected;
  // Rows start collapsed inside groups most users never expand, so their cells
  // mount on first reveal and then stay mounted for the collapse animation.
  const [revealed, setRevealed] = useState(!item.collapsed);
  if (!item.collapsed && !revealed) setRevealed(true);

  const cells = columnKeys.map((key) => {
    const value = item[key];
    const content = !revealed
      ? null
      : typeof value === "function"
        ? value(hasMouse)
        : value;
    const numeric = numericColumns.includes(String(key));
    const style = {
      ...(numeric ? { justifyContent: "flex-end" as const } : {}),
      ...(item.revealIndex
        ? { transitionDelay: `${Math.min(item.revealIndex, 10) * 20}ms` }
        : {}),
    };
    return (
      <Cell
        key={String(key)}
        role="gridcell"
        data-collapsed={
          item.collapsed === undefined ? undefined : String(item.collapsed)
        }
        style={Object.keys(style).length > 0 ? style : undefined}
      >
        {content as React.ReactNode}
      </Cell>
    );
  });

  const rowProps = {
    role: "row" as const,
    "data-selected": selected,
    inert: item.collapsed || undefined,
    onMouseEnter: () => setMouseHovered(true),
    onMouseLeave: () => setMouseHovered(false),
    onFocus: () => setFocusWithin(true),
    onBlur: () => setFocusWithin(false),
  };

  if (item.link) {
    return (
      <Row
        to={item.link}
        onPointerDown={(e) => {
          if (isInAppNavigation(e)) item.onNavigate?.();
        }}
        {...rowProps}
      >
        {cells}
      </Row>
    );
  }

  if (item.onClick) {
    return (
      <ClickableRow {...rowProps} onClick={item.onClick}>
        {cells}
      </ClickableRow>
    );
  }

  return <StaticRow {...rowProps}>{cells}</StaticRow>;
}

// Item identity is the row's memo contract: pages that keep item references
// stable across renders get row re-renders skipped for free.
const TableRow = React.memo(
  TableRowBase,
  (prev, next) =>
    prev.item === next.item &&
    prev.selected === next.selected &&
    prev.numericColumns === next.numericColumns &&
    prev.columnKeys.length === next.columnKeys.length &&
    prev.columnKeys.every((key, i) => key === next.columnKeys[i])
) as typeof TableRowBase;

type Props<T extends Item> = {
  headers: Partial<
    Record<
      Exclude<
        keyof T,
        "key" | "link" | "onClick" | "onNavigate" | "collapsed" | "revealIndex"
      >,
      string
    >
  >;
  items: T[];
  columns?: Partial<
    Record<
      Exclude<
        keyof T,
        "key" | "link" | "onClick" | "onNavigate" | "collapsed" | "revealIndex"
      >,
      ColumnSize
    >
  >;
  style?: React.CSSProperties;
  emptyState?: React.ReactNode;
  sortableColumns?: string[];
  dateColumns?: string[];
  // Columns whose header and cells right-align (for numeric values).
  numericColumns?: readonly string[];
  sortColumn?: string | null;
  sortDirection?: SortDirection;
  onSort?: (column: string) => void;
  headerActions?: React.ReactNode;
  footer?: React.ReactNode;
  hideHeaders?: boolean;
  hideHeadersWhenEmpty?: boolean;
};

export function ListTable<T extends Item>({
  headers,
  items,
  columns = {},
  style = {},
  emptyState = null,
  sortableColumns = [],
  dateColumns = [],
  numericColumns = NO_NUMERIC_COLUMNS,
  sortColumn = null,
  sortDirection = "desc",
  onSort,
  headerActions,
  footer,
  hideHeaders = false,
  hideHeadersWhenEmpty = false,
}: Props<T>) {
  const columnKeys = Object.keys(headers) as Array<
    Exclude<
      keyof T,
      "key" | "link" | "onClick" | "onNavigate" | "collapsed" | "revealIndex"
    >
  >;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const columnTemplate = React.useMemo(() => {
    const last = columnKeys.length - 1;
    return columnKeys
      .map((key, i) => {
        const size = columns[key];
        if (size) return columnSizeValues[size];
        if (i === 0) return columnSizeValues.flex;
        if (i === last) return "max-content";
        return columnSizeValues.sm;
      })
      .join(" ");
  }, [columnKeys, columns]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (items.length === 0) return;
      if (isEditableTarget(e)) return;
      // A table on a hidden tab stays mounted; it must not swallow keys.
      if (!wrapperRef.current?.offsetParent) return;

      const step = (prev: number | null, dir: 1 | -1): number | null => {
        const start = prev === null ? (dir === 1 ? -1 : items.length) : prev;
        for (let i = start + dir; i >= 0 && i < items.length; i += dir) {
          if (!items[i]?.collapsed) return i;
        }
        return prev;
      };

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => step(prev, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => step(prev, -1));
      } else if (e.key === "Enter" && selectedIndex !== null) {
        e.preventDefault();
        const item = items[selectedIndex];
        if (item?.link) {
          item.onNavigate?.();
          history.pushState(history.state, "", item.link);
        } else if (item?.onClick) {
          item.onClick();
        }
      } else if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    },
    [items, selectedIndex]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Reset selection when items change
  const [prevItems, setPrevItems] = useState(items);
  if (prevItems !== items) {
    setPrevItems(items);
    if (selectedIndex !== null) {
      setSelectedIndex(null);
    }
  }

  const showHeader =
    !hideHeaders && !(items.length === 0 && hideHeadersWhenEmpty);

  const headerEl = showHeader ? (
    <Header role="row">
      {columnKeys.map((key) => {
        const keyStr = String(key);
        const isSortable = sortableColumns.includes(keyStr);
        const isActive = sortColumn === keyStr;
        const isLastColumn =
          keyStr === String(columnKeys[columnKeys.length - 1]);
        const isEmptyHeader = headers[key] === "";
        const isNumeric = numericColumns.includes(keyStr);

        return (
          <HeaderCell
            key={keyStr}
            role="columnheader"
            style={
              isLastColumn && isEmptyHeader && headerActions
                ? { justifyContent: "flex-end", gap: 4 }
                : isNumeric
                  ? { justifyContent: "flex-end" }
                  : undefined
            }
          >
            {isLastColumn && isEmptyHeader && headerActions ? (
              headerActions
            ) : isSortable ? (
              <SortableHeaderContent onClick={() => onSort?.(keyStr)}>
                <Text variant="text-small" color="inherit">
                  {headers[key]}
                </Text>
                <SortArrow
                  data-active={isActive}
                  data-direction={
                    isActive
                      ? dateColumns.includes(keyStr)
                        ? sortDirection === "desc"
                          ? "asc"
                          : "desc"
                        : sortDirection
                      : "asc"
                  }
                >
                  <Icon name="arrow-up" size={12} color="currentColor" />
                </SortArrow>
              </SortableHeaderContent>
            ) : (
              <Text variant="text-small" color="var(--text-secondary)">
                {headers[key]}
              </Text>
            )}
          </HeaderCell>
        );
      })}
    </Header>
  ) : null;

  return (
    <Wrapper ref={wrapperRef}>
      {items.length > 0 ? (
        <Table
          role="grid"
          style={{ ...style, gridTemplateColumns: columnTemplate }}
        >
          {headerEl}
          <Body>
            {items.map((item, index) => (
              <TableRow
                key={item.key}
                item={item}
                columnKeys={columnKeys}
                numericColumns={numericColumns}
                selected={selectedIndex === index}
              />
            ))}
          </Body>
        </Table>
      ) : (
        <>
          {headerEl && (
            <Table
              role="grid"
              style={{ ...style, gridTemplateColumns: columnTemplate }}
            >
              {headerEl}
            </Table>
          )}
          {emptyState ? (
            <EmptyStateWrapper>{emptyState}</EmptyStateWrapper>
          ) : null}
        </>
      )}
      {footer ? <Footer>{footer}</Footer> : null}
    </Wrapper>
  );
}
